import { useEffect } from 'react';
import type React from 'react';

import { useLocation, useSearchParams } from 'react-router';

import { Translate } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePetitionEditPageData } from 'src/app/pages/petitionEditV2/pageContext';
import { EndorsementSection } from 'src/app/shared/components/endorsements';
import { ENDORSEMENTS_EDIT_HASH, ENDORSEMENTS_HASH } from 'src/app/shared/constants';
import { useTrackView } from 'src/app/shared/hooks/tracking';
import { useEndorsementsFeature } from 'src/app/shared/hooks/useEndorsementsFeature';
import { useScrollToHash } from 'src/app/shared/hooks/useScrollToHash';
import { useToast } from 'src/app/shared/toast';

import { PetitionContent } from './components/PetitionContent';
import { PetitionData } from './components/PetitionData';

const SCROLL_OFFSET_PX = 40;

export const EditForm: React.FC = () => {
	useTrackView('petition_edit_view');
	const { hash } = useLocation();
	const navigate = useNavigate();
	const track = useTracking();
	const [searchParams, setSearchParams] = useSearchParams();
	const { showToast } = useToast();

	const {
		data: { petition, viewer },
	} = usePetitionEditPageData();
	const { isEnabled: isEndorsementsEnabled } = useEndorsementsFeature(viewer.experiments);

	const endorsementAdded = searchParams.get('endorsementAdded');
	const endorsementUpdated = searchParams.get('endorsementUpdated');
	const shouldScrollToEndorsements = endorsementAdded === 'true' || endorsementUpdated === 'true';

	// Check for endorsementAdded or endorsementUpdated query params and show toast
	useEffect(() => {
		if (endorsementAdded === 'true' || endorsementUpdated === 'true') {
			// Remove the query param from URL
			const newParams = new URLSearchParams(searchParams);
			newParams.delete('endorsementAdded');
			newParams.delete('endorsementUpdated');
			setSearchParams(newParams, { replace: true });

			// Show success toast
			showToast({
				variant: 'success',
				message:
					endorsementUpdated === 'true' ? (
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.success_toast.update_success_toast" />
					) : (
						<Translate value="corgi.petition.starter_dashboard_v2.endorsements.success_toast.message" />
					),
				action: {
					label: <Translate value="corgi.petition.starter_dashboard_v2.endorsements.success_toast.action_button" />,
					onClick: () => {
						void track('view_petition_from_endorsements');
						navigate(`/p/${petition.slug}${ENDORSEMENTS_HASH}`, { preventScrollReset: true });
					},
				},
			});
		}
	}, [endorsementAdded, endorsementUpdated, searchParams, setSearchParams, showToast, navigate, track, petition.slug]);

	// Scroll to endorsements section when needed
	useScrollToHash({
		elementId: ENDORSEMENTS_EDIT_HASH.slice(1),
		targetHash: ENDORSEMENTS_EDIT_HASH,
		enabled: shouldScrollToEndorsements,
		offset: SCROLL_OFFSET_PX,
	});

	// Handle generic URL hash scrolling
	useScrollToHash({
		elementId: hash.slice(1),
		targetHash: hash,
		enabled: !!hash && !shouldScrollToEndorsements,
		offset: SCROLL_OFFSET_PX,
	});

	return (
		<>
			<PetitionContent />
			{isEndorsementsEnabled && <EndorsementSection />}
			<PetitionData />
		</>
	);
};
